import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';

const navItems = [
  { path: '/admin', label: 'Dashboard', icon: 'ri-dashboard-line' },
  { path: '/admin/products', label: 'Products', icon: 'ri-shopping-bag-3-line' },
  { path: '/admin/inquiries', label: 'Inquiries', icon: 'ri-mail-line' },
  { path: '/admin/manufacturer-docs', label: 'Mfr Documents', icon: 'ri-folder-shield-line' },
  { path: '/admin/articles', label: 'Articles', icon: 'ri-article-line' },
  { path: '/admin/settings', label: 'Settings', icon: 'ri-settings-3-line' },
];

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen flex bg-background-100">
      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-foreground-900/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:sticky top-0 left-0 z-50 h-screen w-64 bg-background-50 border-r border-background-200 flex flex-col
        transform transition-transform duration-200 ease-in-out
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-5 border-b border-background-200">
          <Link to="/admin" className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary-500 rounded-lg flex items-center justify-center">
              <span className="text-background-50 font-heading font-bold text-sm">B</span>
            </div>
            <div>
              <p className="text-sm font-heading font-bold text-foreground-900 whitespace-nowrap">BEKAALI</p>
              <p className="text-xs text-foreground-500">Admin Panel</p>
            </div>
          </Link>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer
                  ${isActive
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                  }
                `}
              >
                <i className={`${item.icon} text-lg`} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-background-200 space-y-3">
          <Link
            to="/"
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-foreground-500 hover:bg-background-100 hover:text-foreground-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-external-link-line" />
            Back to Website
          </Link>
          <button
            onClick={handleSignOut}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors w-full whitespace-nowrap cursor-pointer"
          >
            <i className="ri-logout-box-line" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top header */}
        <header className="sticky top-0 z-30 bg-background-50 border-b border-background-200 px-6 py-3 flex items-center justify-between">
          <button
            className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-background-100 text-foreground-600 cursor-pointer"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className="ri-menu-line text-xl" />
          </button>

          <div className="hidden lg:block">
            <h2 className="text-sm font-heading font-semibold text-foreground-900">
              {navItems.find((n) => n.path === location.pathname)?.label || 'Dashboard'}
            </h2>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs text-foreground-500 hidden sm:block">
              {user?.email}
            </span>
            <div className="w-8 h-8 bg-accent-500 rounded-full flex items-center justify-center">
              <span className="text-xs font-bold text-background-50">
                {user?.email?.charAt(0).toUpperCase() || 'A'}
              </span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6">
          {children}
        </main>
      </div>
    </div>
  );
}